import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import Dialog from '@components/2-molecules/Dialog';
import type { AgentSkill } from '@api/agentSkills';
import {
  FormBody,
  FieldGroup,
  FieldHint,
  DetailLabel,
  SkillDescription,
  SkillTools,
  GuidanceBlock,
  FormError,
} from './SkillsPage.styled';

const COPIED_FEEDBACK_MS = 2000;

interface BuiltInSkillDialogProps {
  skill: AgentSkill;
  onClose: () => void;
}

const BuiltInSkillDialog: React.FC<BuiltInSkillDialogProps> = ({ skill, onClose }) => {
  const intl = useIntl();
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
        copyTimeoutRef.current = null;
      }
    };
  }, []);

  const handleCopy = async () => {
    if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    try {
      // navigator.clipboard is undefined outside a secure context, so reading
      // it throws before any promise exists.
      await navigator.clipboard.writeText(skill.instructions ?? '');
    } catch {
      setCopied(false);
      setCopyFailed(true);
      return;
    }
    setCopyFailed(false);
    setCopied(true);
    copyTimeoutRef.current = setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
  };

  return (
    <Dialog
      show
      dataTestId="built-in-skill-dialog"
      headerTitle={skill.name}
      onClose={onClose}
      onCloseIconButtonClick={onClose}
      primaryButtonLabel={
        copied
          ? intl.formatMessage({ id: 'skills.builtIn.copied', defaultMessage: 'Copied' })
          : intl.formatMessage({
              id: 'skills.builtIn.copy',
              defaultMessage: 'Copy instructions',
            })
      }
      onPrimaryButtonClick={handleCopy}
      secondaryButtonLabel={intl.formatMessage({
        id: 'skills.builtIn.close',
        defaultMessage: 'Close',
      })}
      onSecondaryButtonClick={onClose}
      closeOnEscape
    >
      <FormBody>
        <FieldGroup>
          <DetailLabel>
            {intl.formatMessage({ id: 'skills.form.description', defaultMessage: 'Description' })}
          </DetailLabel>
          <SkillDescription>{skill.description}</SkillDescription>
          {skill.tools.length > 0 && (
            <SkillTools>
              {intl.formatMessage(
                { id: 'skills.builtIn.tools', defaultMessage: 'Tools: {tools}' },
                { tools: skill.tools.join(', ') },
              )}
            </SkillTools>
          )}
        </FieldGroup>

        <FieldGroup>
          <DetailLabel>
            {intl.formatMessage({ id: 'skills.form.instructions', defaultMessage: 'Instructions' })}
          </DetailLabel>
          <GuidanceBlock
            tabIndex={0}
            role="region"
            aria-label={intl.formatMessage({
              id: 'skills.form.instructions',
              defaultMessage: 'Instructions',
            })}
          >
            {skill.instructions}
          </GuidanceBlock>
          <FieldHint>
            {intl.formatMessage({
              id: 'skills.builtIn.readOnlyHint',
              defaultMessage:
                'Read-only. Copy this into a new organization skill to experiment with changes.',
            })}
          </FieldHint>
          {copyFailed && (
            <FormError role="alert">
              {intl.formatMessage({
                id: 'skills.builtIn.copyFailed',
                defaultMessage: 'Could not copy to the clipboard. Select the text and copy it manually.',
              })}
            </FormError>
          )}
        </FieldGroup>
      </FormBody>
    </Dialog>
  );
};

export default BuiltInSkillDialog;
